import Alpine from 'alpinejs'
import Cookies from 'js-cookie'
import { formatPrice } from '@/utils/format-price'

import { getAccessToken } from '@/clients/commercelayer/get-access-token'
import { createClient, type CommerceClient } from '@/clients/commercelayer/client'
import {
  CART_INCLUDES,
  CART_NAME,
  LINE_ITEM_ATTRS,
  ORDER_ATTRS,
} from '@/clients/commercelayer/constants'
import { getNormalizedCartSummary, type CLOrder } from './commerce/normalize-cart-summary'

type Summary = {
  id: string
  line_items: {
    id: string
    sku_code: string
  }[]
} | null

Alpine.data('commerce', () => ({
  formatPrice,
  loading: false,
  cl: null as null | CommerceClient,
  summary: null as Summary,
  orderId: undefined as undefined | string,
  async init() {
    const { accessToken } = await getAccessToken()

    if (!accessToken) {
      console.error('Failed to retrieve access token')
      return
    }

    this.cl = createClient(accessToken)
    this.orderId = Cookies.get(CART_NAME)
    if (this.orderId) {
      try {
        const cart = await this.getCart(this.orderId)
        if (cart?.status === 'draft' || cart?.status === 'pending') {
          this.$dispatch('setCart', cart)
        } else {
          Cookies.remove(CART_NAME)
        }
      } catch (error) {
        console.error('Error getting cart', error)
      }
    }
  },
  async createCart() {
    try {
      if (!this.cl) return
      this.loading = true
      const { data } = (await this.cl.post(`/orders`, {
        data: {
          type: 'orders',
        },
      })) as { data: { id: string } }

      if (data?.id) {
        this.orderId = data?.id
        Cookies.set(CART_NAME, this.orderId)
      }
    } catch (error) {
      console.error(error)
    } finally {
      this.loading = false
    }
  },
  async getCart(id: string) {
    if (!this.cl) return

    const include = `include=${CART_INCLUDES}`
    const orderFields = `fields[orders]=${ORDER_ATTRS}`
    const lineItemFields = `fields[line_items]=${LINE_ITEM_ATTRS}`

    const endpoint = `/orders/${id}?${include}&${orderFields}&${lineItemFields}`

    const cartSummary = (await this.cl.get(endpoint)) as CLOrder

    return getNormalizedCartSummary(cartSummary)
  },
  commerceHooks: {
    async ['@updateLineItem']({ detail }: { detail: { id: string; quantity: number } }) {
      try {
        if (!this.cl) return
        this.loading = true
        const { id, quantity } = detail

        await this.cl.patch(`/line_items/${id}`, {
          data: {
            type: 'line_items',
            id,
            attributes: {
              quantity,
            },
          },
        })

        if (this.orderId) {
          await this.getCart(this.orderId).then((cart) => this.$dispatch('setCart', cart))
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async ['@onDeleteLineItem']({ detail }: { detail: { id: string; quantity: number } }) {
      try {
        if (!this.cl) return
        this.loading = true
        const { id } = detail

        await this.cl.delete(`/line_items/${id}`)

        if (this.orderId) {
          await this.getCart(this.orderId).then((cart) => this.$dispatch('setCart', cart))
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async ['@addToCart']({
      detail,
    }: {
      detail: {
        code: string
        quantity: number
        name?: string
        image_url?: string
        metadata: Record<string, string>
      }
    }) {
      if (!this.cl) return
      try {
        this.loading = true

        if (!this.orderId) {
          await this.createCart()
        }

        const { code, quantity, name, image_url, metadata } = detail

        await this.cl.post(`/line_items`, {
          data: {
            type: 'line_items',
            attributes: {
              quantity,
              name,
              image_url,
              metadata,
              sku_code: code,
              _update_quantity: true,
            },
            relationships: {
              order: {
                data: {
                  type: 'orders',
                  id: this.orderId,
                },
              },
            },
          },
        })

        if (this.orderId) {
          const cart = await this.getCart(this.orderId)
          this.$dispatch('setCart', cart)

          const updatedDetail = {
            ...detail,
            checkout_url: cart?.checkout_url,
          }
          this.$dispatch('setNotification', updatedDetail)
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async ['@updateOrderMetadata']({ detail }: { detail: Record<string, string> }) {
      if (!this.cl || !this.orderId) return

      try {
        this.loading = true

        await this.cl.patch(`/orders/${this.orderId}`, {
          data: {
            type: 'orders',
            id: this.orderId,
            attributes: {
              metadata: detail,
            },
          },
        })
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
  },
}))
